// src/pages/task/taskItemDetails/TaskDetails.tsx

import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import classes from './TaskDetails.module.sass'
import { useTaskDetails } from './useTaskDetails'
import { TaskStatus } from '../../../common/types/enums'
import { Button } from '../../../common/components/button/Button'
import { TASK } from '../../../common/utils/constants'
import { useCopyToClipboard } from '../../../common/hooks/useCopyToClipboard'
import { SkeletonLoader } from '../../../common/components/skeletonLoader/SkeletonLoader'

const statusText = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.PENDING:
      return 'Свободно'
    case TaskStatus.TAKEN:
      return 'Взято'
    case TaskStatus.IN_PROGRESS:
      return 'В пути'
    case TaskStatus.ARRIVED:
      return 'Прибыл'
    case TaskStatus.DELIVERED:
      return 'Доставлено'
    case TaskStatus.CANCELLED:
      return 'Отменено'
    default:
      return '—'
  }
}

const paymentText = (method: number): string => {
  switch (method) {
    case 0:
      return 'Наличными'
    case 1:
      return 'Картой'
    case 2:
      return 'Онлайн'
    default:
      return '—'
  }
}

const normalizePhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '')
  if (digits.startsWith('8') && digits.length === 11) {
    return `+7${digits.slice(1)}`
  }
  if (digits.startsWith('7') && digits.length === 11) {
    return `+${digits}`
  }
  return `+${digits}`
}

const formatPhone = (phone: string): string => {
  const normalized = normalizePhone(phone)
  if (normalized.length === 12) {
    return `+7 (${normalized.slice(2, 5)}) ${normalized.slice(5, 8)}-${normalized.slice(8, 10)}-${normalized.slice(10)}`
  }
  return phone
}

const buildYandexUrl = (
  fromLat: number | null,
  fromLng: number | null,
  toLat: number | null,
  toLng: number | null,
): string | null => {
  if (!toLat || !toLng) return null
  if (fromLat && fromLng) {
    return `https://yandex.ru/maps/?rtext=${fromLat},${fromLng}~${toLat},${toLng}&rtt=auto`
  }
  return `https://yandex.ru/maps/?rtext=~${toLat},${toLng}&rtt=auto`
}

const buildGoogleUrl = (
  fromLat: number | null,
  fromLng: number | null,
  toLat: number | null,
  toLng: number | null,
): string | null => {
  if (!toLat || !toLng) return null
  if (fromLat && fromLng) {
    return `https://www.google.com/maps/dir/?api=1&origin=${fromLat},${fromLng}&destination=${toLat},${toLng}&travelmode=DRIVING`
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${toLat},${toLng}&travelmode=DRIVING`
}

const buildDoubleGisUrl = (
  fromLat: number | null,
  fromLng: number | null,
  toLat: number | null,
  toLng: number | null,
): string | null => {
  if (!toLat || !toLng) return null
  if (fromLat && fromLng) {
    return `https://2gis.ru/directions/points/${fromLng},${fromLat}|${toLng},${toLat}`
  }
  return `https://2gis.ru/directions/points/${toLng},${toLat}`
}

export const TaskDetails = () => {
  const navigate = useNavigate()
  const { task, loading, error, takeTask, updateStatus } = useTaskDetails()
  const { copy, copied } = useCopyToClipboard()

  if (!task) {
    if (error) {
      return <div className={classes.error}>{error}</div>
    }
    if (loading) {
      return (
        <div className={classes.container}>
          <Helmet>
            <title>Задание</title>
          </Helmet>

          {/* Пустой блок высотой под весь контент */}
          <div className={classes.skeletonPlaceholder} />
        </div>
      )
    }
    return <div className={classes.message}>Задание не найдено</div>
  }

  const order = task.order
  const store = order?.store
  const storeAddress = store?.address
  const fullAddress = order?.fullAddress
  const address = fullAddress?.address

  const fromLat = storeAddress?.latitude ? Number(storeAddress.latitude) : null
  const fromLng = storeAddress?.longitude ? Number(storeAddress.longitude) : null
  const toLat = address?.latitude ? Number(address.latitude) : null
  const toLng = address?.longitude ? Number(address.longitude) : null

  const yandexUrl = buildYandexUrl(fromLat, fromLng, toLat, toLng)
  const googleUrl = buildGoogleUrl(fromLat, fromLng, toLat, toLng)
  const doubleGisUrl = buildDoubleGisUrl(fromLat, fromLng, toLat, toLng)

  const isPending = task.status === TaskStatus.PENDING
  const isMyTask = !isPending && task.executorId !== null
  const clientPhone = order?.phone_number || ''
  const normalizedPhone = clientPhone ? normalizePhone(clientPhone) : ''

  return (
    <div className={classes.container}>
      <Helmet>
        <title>{task.id ? `Задание #${task.id}` : 'Задание'}</title>
        <meta name="description" content="Детали задания" />
      </Helmet>

      <div className={classes.header}>
        <button className={classes.backButton} onClick={() => navigate(TASK)}>
          ← Назад
        </button>
        <div className={classes.title}>Задание #{task.id}</div>
        <div className={classes.status}>{statusText(task.status)}</div>
      </div>

      <div className={classes.section}>
        <div className={classes.clusterName}>{task.cluster?.name}</div>
      </div>

      {store && (
        <div className={classes.section}>
          <div className={classes.label}>Откуда</div>
          <div className={classes.value}>{store.name}</div>
          {storeAddress?.value && <div className={classes.value}>{storeAddress.value}</div>}
          {store.phone_number && (
            <div className={classes.phoneRow}>
              <a href={`tel:${normalizePhone(store.phone_number)}`} className={classes.link}>
                {formatPhone(store.phone_number)}
              </a>
              <a href={`tel:${normalizePhone(store.phone_number)}`} className={classes.callButton}>
                Позвонить
              </a>
            </div>
          )}
        </div>
      )}

      {address && (
        <div className={classes.section}>
          <div className={classes.label}>Куда</div>

          <div className={classes.addressRow}>
            <div className={classes.value}>{address.value}</div>
            <button
              className={classes.copyButton}
              onClick={() => copy(address.value)}
              title="Скопировать адрес"
            >
              {copied ? 'Скопировано' : 'Копировать'}
            </button>
          </div>

          {(fullAddress?.entrance ||
            fullAddress?.floor ||
            fullAddress?.apartment ||
            fullAddress?.intercom) && (
            <div className={classes.addressDetails}>
              {fullAddress?.entrance && <span>Подъезд: {fullAddress.entrance}</span>}
              {fullAddress?.floor && <span>Этаж: {fullAddress.floor}</span>}
              {fullAddress?.apartment && <span>Кв/офис: {fullAddress.apartment}</span>}
              {fullAddress?.intercom && <span>Домофон: {fullAddress.intercom}</span>}
            </div>
          )}

          {(yandexUrl || googleUrl || doubleGisUrl) && (
            <div className={classes.navLinks}>
              {yandexUrl && (
                <a href={yandexUrl} target="_blank" rel="noopener noreferrer">
                  Яндекс
                </a>
              )}
              {googleUrl && (
                <a href={googleUrl} target="_blank" rel="noopener noreferrer">
                  Google
                </a>
              )}
              {doubleGisUrl && (
                <a href={doubleGisUrl} target="_blank" rel="noopener noreferrer">
                  2ГИС
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {isMyTask && normalizedPhone && (
        <div className={classes.section}>
          <div className={classes.label}>Телефон клиента</div>
          <div className={classes.phoneRow}>
            <a href={`tel:${normalizedPhone}`} className={classes.link}>
              {formatPhone(clientPhone)}
            </a>
            <a href={`tel:${normalizedPhone}`} className={classes.callButton}>
              Позвонить
            </a>
          </div>
        </div>
      )}

      {order?.notes && (
        <div className={classes.section}>
          <div className={classes.label}>Комментарий</div>
          <div className={classes.value}>{order.notes}</div>
        </div>
      )}

      <div className={classes.section}>
        <div className={classes.row}>
          <div className={classes.label}>Оплата исполнитею</div>
          <div className={classes.value}>{task.fee} ₽</div>
        </div>
        <div className={classes.row}>
          <div className={classes.label}>Сумма заказа</div>
          <div className={classes.value}>{order?.total} ₽</div>
        </div>
        <div className={classes.row}>
          <div className={classes.label}>Способ оплаты</div>
          <div className={classes.value}>{paymentText(order?.paymentMethod ?? -1)}</div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.row}>
          <div className={classes.label}>Создано</div>
          <div className={classes.value}>
            {format(new Date(task.createdAt), 'dd.MM.yyyy HH:mm')}
          </div>
        </div>
      </div>

      {order?.items && order.items.length > 0 && (
        <div className={classes.section}>
          <div className={classes.label}>Состав заказа</div>
          {order.items.map((item, idx) => (
            <div key={idx} className={classes.item}>
              <span>{item.name || `Товар #${item.itemId}`}</span>
              <span>× {item.quantity}</span>
            </div>
          ))}
        </div>
      )}

      <div className={classes.actions}>
        {isPending && <Button text="Взять в работу" onClick={takeTask} />}

        {task.status === TaskStatus.TAKEN && (
          <Button text="В путь" onClick={() => updateStatus(TaskStatus.IN_PROGRESS)} />
        )}

        {task.status === TaskStatus.IN_PROGRESS && (
          <Button text="Прибыл" onClick={() => updateStatus(TaskStatus.ARRIVED)} />
        )}

        {task.status === TaskStatus.ARRIVED && (
          <Button text="Доставлено" onClick={() => updateStatus(TaskStatus.DELIVERED)} />
        )}
      </div>
    </div>
  )
}
